import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useTranslation } from 'react-i18next';
import { openQuestionApiStreaming, saveOpenQuestionApi, getOpenQuestionHistoryApi, getSingleOpenQuestionApi, deleteOpenQuestionApi } from '../../services/api';
import SearchBox from '../search/SearchBox';

const OpenQuestion = () => {
    const { t } = useTranslation();
    const {
        currentQCM,
        sessionContext,
        loading,
        setQcmData,  // Pour définir directement les données QCM
        setCurrentQCMIndex,  // Pour définir l'index
        setDisplayMode  // Pour changer l'affichage
    } = useQCM();

    const [question, setQuestion] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showInput, setShowInput] = useState(false);
    const [showQuestionsList, setShowQuestionsList] = useState(false);
    const [postIts, setPostIts] = useState([]); // Array to store multiple post-its
    const [savedQuestions, setSavedQuestions] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState(''); // For filtering questions
    const textInputRef = useRef(null);
    
    // Fonction pour gérer la sélection d'un item depuis la recherche
    const handleSearchItemSelect = useCallback(async (item, questionType) => {
        if (questionType === 'qcm') {
            try {
                console.log('QCM sélectionné depuis la recherche:', item);
                
                // Utiliser directement l'ID du QCM avec le système existant
                const qcmId = item.id || item._id; // Utiliser id (API de recherche) ou _id en fallback
                console.log('🎯 Chargement direct du QCM avec ID:', qcmId);
                
                // Créer un événement personnalisé pour charger le QCM spécifique
                const qcmEvent = new CustomEvent('loadSpecificQCMFromSearch', { 
                    detail: {
                        qcmId: qcmId
                        // Utiliser le système existant - pas besoin de passer les données
                    }
                });
                
                // Envoyer l'événement au niveau global
                window.dispatchEvent(qcmEvent);
                
                console.log('✅ Événement loadSpecificQCMFromSearch envoyé avec ID:', qcmId);
                
            } catch (error) {
                console.error('Erreur lors du chargement du QCM:', error);
            }
        } else if (questionType === 'lab') {
            // Charger le Lab sélectionné
            try {
                console.log('Lab sélectionné depuis la recherche:', item);
                
                // Pour les labs, créer un événement similaire
                const labEvent = new CustomEvent('loadSpecificLabFromSearch', { 
                    detail: {
                        labId: item._id || item.id, // Utiliser _id (MongoDB) ou id en fallback
                        labData: item
                    }
                });
                window.dispatchEvent(labEvent);
                
            } catch (error) {
                console.error('Erreur lors du chargement du Lab:', error);
            }
        }
    }, []);

    // Character limit for question (increased to 45)
    const MAX_QUESTION_LENGTH = 45; // Backend limit

    useEffect(() => {
        if (showInput && textInputRef.current) {
            textInputRef.current.focus();
        }
    }, [showInput]);

    // Load saved questions on component mount
    useEffect(() => {
        loadSavedQuestions();
    }, []);

    // Generate random post-it color
    const generatePostItColor = () => {
        const colors = [
            { bg: 'bg-yellow-200', border: 'border-yellow-300', header: 'bg-yellow-300' },
            { bg: 'bg-pink-200', border: 'border-pink-300', header: 'bg-pink-300' },
            { bg: 'bg-blue-200', border: 'border-blue-300', header: 'bg-blue-300' },
            { bg: 'bg-green-200', border: 'border-green-300', header: 'bg-green-300' },
            { bg: 'bg-purple-200', border: 'border-purple-300', header: 'bg-purple-300' },
            { bg: 'bg-orange-200', border: 'border-orange-300', header: 'bg-orange-300' },
            { bg: 'bg-cyan-200', border: 'border-cyan-300', header: 'bg-cyan-300' },
            { bg: 'bg-rose-200', border: 'border-rose-300', header: 'bg-rose-300' }
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const createNewPostIt = (questionText, answerText, id = null) => {
        const newPostIt = {
            id: id || Date.now(),
            question: questionText,
            answer: answerText,
            color: generatePostItColor(),
            position: { 
                x: window.innerWidth / 2 - 200, // Center horizontally
                y: window.innerHeight / 2 - 150 + (postIts.length * 30) // Center vertically with offset
            },
            isMinimized: false,
            fontSize: 16,
            isDragging: false,
            dragStart: { x: 0, y: 0 },
            timestamp: new Date().toISOString()
        };
        return newPostIt;
    };

    const deletePostIt = async (postItId) => {
        try {
            const user = sessionContext?.user || 'anonymous';
            
            // Delete from database
            await deleteOpenQuestionApi(postItId, user);
            
            // Remove from local state
            setPostIts(postIts.filter(p => p.id !== postItId));
            
            // Update saved questions
            const updatedSaved = savedQuestions.filter(item => item.id !== postItId);
            setSavedQuestions(updatedSaved);
            
            console.log('Post-it deleted permanently from database');
        } catch (error) {
            console.error('Error deleting post-it:', error);
            // Still remove from local state even if DB deletion fails
            setPostIts(postIts.filter(p => p.id !== postItId));
            const updatedSaved = savedQuestions.filter(item => item.id !== postItId);
            setSavedQuestions(updatedSaved);
        }
    };

    const loadSavedQuestions = async () => {
        try {
            const user = sessionContext?.user || 'anonymous';
            const examCode = currentQCM?.exam_code || sessionContext?.exam || 'default-exam';
            const response = await getOpenQuestionHistoryApi(user, 50, examCode);
            if (response.success) {
                // Filter by user and exam code
                const filteredQuestions = response.questions.filter(q => 
                    q.user === user && q.exam_code === examCode
                );
                setSavedQuestions(filteredQuestions);
            }
        } catch (error) {
            console.error('Error loading saved questions:', error);
            // Fallback to localStorage
            const saved = localStorage.getItem('openQuestionHistory');
            if (saved) {
                const allQuestions = JSON.parse(saved);
                const user = sessionContext?.user || 'anonymous';
                const examCode = currentQCM?.exam_code || sessionContext?.exam || 'default-exam';
                const filteredQuestions = allQuestions.filter(q => 
                    q.user === user && q.exam_code === examCode
                );
                setSavedQuestions(filteredQuestions);
            }
        }
    };

    // Function to load full content of a specific question from backend
    const loadQuestionContent = async (questionId) => {
        try {
            const user = sessionContext?.user || 'anonymous';
            const examCode = currentQCM?.exam_code || sessionContext?.exam || 'default-exam';
            const response = await getSingleOpenQuestionApi(questionId, user, examCode);
            if (response.success) {
                return response.question;
            }
        } catch (error) {
            console.error('Error loading question content:', error);
        }
        return null;
    };

    // Filter questions based on search query
    const filteredQuestions = savedQuestions.filter(item => 
        item.question.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const saveQuestion = async (questionData) => {
        try {
            const user = sessionContext?.user || 'anonymous';
            const examCode = currentQCM?.exam_code || sessionContext?.exam || 'default-exam';
            const saveData = {
                question: questionData.question,
                answer: questionData.answer,
                user: user,
                exam_code: examCode,
                exam_name: currentQCM?.exam_title || sessionContext?.exam || 'Default Exam',
                domain: currentQCM?.metadata?.Domain || '',
                topic: currentQCM?.metadata?.topic || ''
            };
            
            const response = await saveOpenQuestionApi(saveData);
            console.log('🔥 DEBUG saveQuestion - response:', response);
            if (response.success) {
                // Update local state with the correct MongoDB ID
                const updated = [...savedQuestions, { ...questionData, id: response.id }];
                setSavedQuestions(updated);
                
                // Also update the postIts with the correct ID
                setPostIts(prevPostIts => {
                    const updatedPostIts = prevPostIts.map(item => 
                        item.question === questionData.question && item.answer === questionData.answer
                            ? { ...item, id: response.id }
                            : item
                    );
                    console.log('🔥 DEBUG saveQuestion - prevPostIts:', prevPostIts);
                    console.log('🔥 DEBUG saveQuestion - updatedPostIts:', updatedPostIts);
                    return updatedPostIts;
                });
            }
        } catch (error) {
            console.error('Error saving question:', error);
            // Fallback to localStorage
            const updated = [...savedQuestions, questionData];
            setSavedQuestions(updated);
            localStorage.setItem('openQuestionHistory', JSON.stringify(updated));
        }
    };



    const handleQuestionChange = (e) => {
        const value = e.target.value;
        if (value.length <= MAX_QUESTION_LENGTH) {
            setQuestion(value);
            setErrorMessage(''); // Clear error when typing
        } else {
            setErrorMessage(`Maximum ${MAX_QUESTION_LENGTH} caractères autorisés`);
        }
    };

    const handleQuestionSubmit = async () => {
        if (!question.trim() || isLoading) return;

        // Check length before submitting
        if (question.trim().length > MAX_QUESTION_LENGTH) {
            setErrorMessage(`Maximum ${MAX_QUESTION_LENGTH} caractères autorisés`);
            return;
        }

        setIsLoading(true);
        setErrorMessage(''); // Clear any previous error

        // Store question and create post-it before try/catch
        const questionText = question.trim();
        const loadingPostIt = createNewPostIt(questionText, 'Génération de la réponse en cours...');
        console.log('🔥 DEBUG handleQuestionSubmit - loadingPostIt:', loadingPostIt);
        setPostIts(prevPostIts => {
            const updated = [...prevPostIts, loadingPostIt];
            console.log('🔥 DEBUG handleQuestionSubmit - prevPostIts:', prevPostIts);
            console.log('🔥 DEBUG handleQuestionSubmit - updated:', updated);
            return updated;
        });
        setQuestion(''); // Clear input immediately
        setShowInput(false); // Hide input immediately

        try {
            // Prepare the payload according to the specification
            const payload = {
                // From current QCM displayed (with fallbacks)
                Domain: currentQCM?.metadata?.Domain || 'General',
                topic: currentQCM?.metadata?.topic || 'General Topic',
                'Topic_Occurrence_%': currentQCM?.metadata?.['Topic_Occurrence_%'] || 0,
                question: currentQCM?.qcm?.question || currentQCM?.question || 'General context question',
                
                // Priority set in user context (default to 1 as per spec)
                priority: sessionContext?.priority || 1,
                
                // Subject to answer (user input)
                subject_to_answer: questionText,
                
                // Exam name and editor solution from context (with fallbacks)
                exam_name: currentQCM?.exam_title || sessionContext?.exam || 'General Learning',
                editor_solution: currentQCM?.editor_solution || sessionContext?.editor_solution || 'Learning Context',
                
                // History of previous questions
                history: [],

                // Additional context fields
                user: sessionContext?.user || 'learner1',
                sessionId: sessionContext?.sessionId || '111222',
                exam_id: sessionContext?.exam || 'general-learning'
            };

            console.log('Sending openQuestion payload:', payload);
            
            // Use streaming API with ref to track answer
            const streamingAnswerRef = { current: '' };
            
            await openQuestionApiStreaming(
                payload,
                // onMessage - handle streaming chunks
                (data) => {
                    console.log('🔥 DEBUG onMessage - data:', data);
                    if (data.type === 'status') {
                        console.log('🔥 DEBUG onMessage - status:', data.message);
                        // Update post-it with status
                        updatePostIt(loadingPostIt.id, { ...loadingPostIt, answer: data.message || 'Traitement en cours...' });
                    } else if (data.type === 'chunk') {
                        console.log('🔥 DEBUG onMessage - chunk:', data.text);
                        // Append streaming text chunks
                        streamingAnswerRef.current += data.text + ' ';
                        console.log('🔥 DEBUG onMessage - streamingAnswerRef.current:', streamingAnswerRef.current);
                        updatePostIt(loadingPostIt.id, { ...loadingPostIt, answer: streamingAnswerRef.current.trim() });
                    }
                },
                // onError - handle errors
                (error) => {
                    console.error('Streaming error:', error);
                    // Update existing post-it with error message
                    updatePostIt(loadingPostIt.id, { ...loadingPostIt, answer: 'Erreur de connexion. Veuillez vérifier votre connexion et réessayer.' });
                    setIsLoading(false);
                },
                // onComplete - handle completion
                (finalData) => {
                    console.log('🔥 DEBUG onComplete - finalData:', finalData);
                    console.log('🔥 DEBUG onComplete - streamingAnswerRef.current:', streamingAnswerRef.current);
                    console.log('🔥 DEBUG onComplete - loadingPostIt:', loadingPostIt);
                    
                    if (finalData?.success) {
                        const finalAnswer = streamingAnswerRef.current.trim();
                        console.log('🔥 DEBUG onComplete - finalAnswer:', finalAnswer);
                        
                        // Update existing post-it with final answer
                        updatePostIt(loadingPostIt.id, { 
                            ...loadingPostIt, 
                            answer: finalAnswer
                        });
                        
                        // Auto-save the question (async, don't wait for it)
                        saveQuestion({
                            id: loadingPostIt.id,
                            question: loadingPostIt.question,
                            answer: finalAnswer,
                            timestamp: loadingPostIt.timestamp
                        }).then(() => {
                            console.log('🔥 DEBUG onComplete - Question saved successfully');
                        }).catch((error) => {
                            console.error('🔥 DEBUG onComplete - Error saving question:', error);
                        });
                    } else {
                        console.log('🔥 DEBUG onComplete - Error case, finalData:', finalData);
                        // Handle specific backend errors
                        let errorMessage = 'Erreur lors de la génération de la réponse. Veuillez réessayer.';
                        if (finalData?.error?.includes('subject_to_answer must be 45 characters or less')) {
                            errorMessage = `Question trop longue (max ${MAX_QUESTION_LENGTH} caractères)`;
                        }
                        // Update existing post-it with error message
                        updatePostIt(loadingPostIt.id, { 
                            ...loadingPostIt, 
                            answer: errorMessage
                        });
                    }
                    setIsLoading(false);
                }
            );

        } catch (error) {
            console.error('Error submitting open question:', error);
            // Update existing post-it with error message
            updatePostIt(loadingPostIt.id, { ...loadingPostIt, answer: 'Erreur de connexion. Veuillez vérifier votre connexion et réessayer.' });
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleQuestionSubmit();
        }
        if (e.key === 'Escape') {
            setShowInput(false);
        }
    };



    const formatAnswer = (text) => {
        // Parse the structured response with expand/collapse sections
        const sections = [];
        const lines = text.split('\n');
        let currentSection = null;
        let currentContent = [];
        
        // Default expanded sections - make the first section always expanded
        const defaultExpanded = ['Vulgarized Summary', 'Purpose', 'Summary', 'Explanation', 'Overview'];
        
        lines.forEach((line, index) => {
            const trimmedLine = line.trim();
            
            // Check if line is a section header (starts with ** and ends with **)
            const sectionMatch = trimmedLine.match(/^\*\*(.*?)\*\*$/);
            
            if (sectionMatch) {
                // Save previous section if exists
                if (currentSection) {
                    sections.push({
                        title: currentSection,
                        content: currentContent.join('\n'),
                        isExpanded: defaultExpanded.includes(currentSection)
                    });
                }
                
                // Start new section
                currentSection = sectionMatch[1];
                currentContent = [];
            } else if (currentSection && trimmedLine) {
                // Add content to current section
                currentContent.push(trimmedLine);
            }
        });
        
        // Add the last section
        if (currentSection) {
            sections.push({
                title: currentSection,
                content: currentContent.join('\n'),
                isExpanded: defaultExpanded.includes(currentSection)
            });
        }
        
        // Ensure the first section is always expanded
        if (sections.length > 0) {
            sections[0].isExpanded = true;
        }
        
        return sections;
    };

    // Component for individual Post-it
    const PostItComponent = ({ postIt, onDelete, onUpdate }) => {
        const [isDragging, setIsDragging] = useState(false);
        const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
        
        // Initialize expanded sections ONCE with default values
        const [expandedSections, setExpandedSections] = useState(() => {
            const defaultExpanded = ['Vulgarized Summary', 'Purpose', 'Summary', 'Explanation', 'Overview'];
            const sections = formatAnswer(postIt.answer);
            const initialState = {};
            sections.forEach((section, index) => {
                // First section is always expanded, others follow default rules
                initialState[section.title] = index === 0 || defaultExpanded.includes(section.title);
            });
            return initialState;
        });

        const handleMouseDown = (e) => {
            // Only allow drag from the header area (not from buttons or content)
            if (e.target.closest('button') || e.target.closest('.post-it-content')) {
                return;
            }
            
            e.preventDefault();
            e.stopPropagation();
            
            const startX = e.clientX - postIt.position.x;
            const startY = e.clientY - postIt.position.y;
            
            setIsDragging(true);
            setDragStart({ x: startX, y: startY });

            const handleMouseMove = (moveEvent) => {
                moveEvent.preventDefault();
                moveEvent.stopPropagation();
                const newPosition = {
                    x: moveEvent.clientX - startX,
                    y: moveEvent.clientY - startY
                };
                onUpdate(postIt.id, {
                    ...postIt,
                    position: newPosition
                });
            };

            const handleMouseUp = (upEvent) => {
                upEvent.preventDefault();
                upEvent.stopPropagation();
                setIsDragging(false);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        };

        return (
            <div
                id={`postit-${postIt.id}`}
                className={`fixed z-40 ${postIt.color.bg} shadow-2xl rounded-xl border-2 ${postIt.color.border} transition-none`}
                style={{
                    left: postIt.position.x,
                    top: postIt.position.y,
                    width: postIt.isMinimized ? '200px' : '400px',
                    minHeight: postIt.isMinimized ? '60px' : '200px',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1)',
                    userSelect: 'none',
                    willChange: 'transform'
                }}
                onMouseDown={handleMouseDown}
            >
                {/* Post-it Header */}
                <div 
                    className={`${postIt.color.header} p-3 rounded-t-xl flex items-center justify-between shadow-sm cursor-pointer`}
                    onClick={() => onUpdate(postIt.id, { ...postIt, isMinimized: !postIt.isMinimized })}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-800">
                            {postIt.question}
                        </span>
                        {!postIt.isMinimized && (
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onUpdate(postIt.id, { ...postIt, fontSize: Math.max(12, postIt.fontSize - 2) });
                                    }}
                                    className="text-xs bg-opacity-80 hover:bg-opacity-100 text-gray-800 px-2 py-1 rounded-md shadow-sm transition-all"
                                    style={{ backgroundColor: postIt.color.header || 'rgba(255, 255, 255, 0.8)' }}
                                >
                                    A-
                                </button>
                                <span className="text-xs text-gray-600 font-medium">{postIt.fontSize}px</span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onUpdate(postIt.id, { ...postIt, fontSize: Math.min(24, postIt.fontSize + 2) });
                                    }}
                                    className="text-xs bg-opacity-80 hover:bg-opacity-100 text-gray-800 px-2 py-1 rounded-md shadow-sm transition-all"
                                    style={{ backgroundColor: postIt.color.header || 'rgba(255, 255, 255, 0.8)' }}
                                >
                                    A+
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(postIt.id);
                            }}
                            className="text-xs bg-red-500 hover:bg-red-600 px-2 py-1 rounded-md shadow-sm transition-all text-white"
                            title="Delete permanently"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Post-it Content */}
                {!postIt.isMinimized && (
                    <div className="p-4 post-it-content">
                        <div className="text-gray-800 mb-3 leading-relaxed" style={{ fontSize: `${postIt.fontSize}px`, lineHeight: '1.5' }}>
                            {(() => {
                                const sections = formatAnswer(postIt.answer);
                                if (sections.length === 0) {
                                    // Fallback for non-structured content
                                    return (
                                        <div 
                                            dangerouslySetInnerHTML={{ 
                                                __html: postIt.answer.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                                            }} 
                                        />
                                    );
                                }
                                
                                return sections.map((section, index) => {
                                    const isExpanded = expandedSections[section.title] !== undefined 
                                        ? expandedSections[section.title] 
                                        : false;
                                    
                                    const handleSectionToggle = () => {
                                        setExpandedSections(prev => ({
                                            ...prev,
                                            [section.title]: !isExpanded
                                        }));
                                    };
                                    
                                    return (
                                        <div key={`section-${section.title}-${index}`} className="mb-3 last:mb-0">
                                            <button
                                                onClick={handleSectionToggle}
                                                className="flex items-center justify-between w-full text-left font-semibold text-gray-800 hover:text-gray-900 transition-colors mb-1 select-none cursor-pointer"
                                                type="button"
                                            >
                                                <span className="text-sm font-bold">{section.title}</span>
                                                <svg 
                                                    className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                                                    fill="none" 
                                                    stroke="currentColor" 
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                            
                                            {isExpanded && section.content && (
                                                <div className="ml-2 text-sm text-gray-700">
                                                    {section.content.split('\n').map((line, lineIndex) => {
                                                        const trimmedLine = line.trim();
                                                        if (!trimmedLine) return null;
                                                        
                                                        if (trimmedLine.startsWith('- ')) {
                                                            return (
                                                                <div key={lineIndex} className="ml-2 mb-1">
                                                                    {trimmedLine}
                                                                </div>
                                                            );
                                                        }
                                                        return (
                                                            <div key={lineIndex} className="mb-1">
                                                                {trimmedLine}
                                                            </div>
                                                        );
                                                    }).filter(Boolean)}
                                                </div>
                                            )}
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const updatePostIt = (postItId, updatedPostIt) => {
        console.log('🔥 DEBUG updatePostIt - postItId:', postItId, 'updatedPostIt:', updatedPostIt);
        setPostIts(prevPostIts => {
            const updated = prevPostIts.map(p => p.id === postItId ? updatedPostIt : p);
            console.log('🔥 DEBUG updatePostIt - prevPostIts:', prevPostIts);
            console.log('🔥 DEBUG updatePostIt - updated:', updated);
            return updated;
        });
    };

    if (!currentQCM) {
        return null; // Don't display if no QCM is loaded
    }

    return (
        <>
            <div className="flex items-center justify-end mb-1 gap-4">
                {/* Search box and Ask something button container */}
                <div className="flex items-center gap-4">
                    {/* SearchBox - positioned to the left of "Ask something" */}
                    <div className="flex-1 max-w-md">
                        <SearchBox
                            examType={sessionContext?.examType || 'qcm'}
                            examId={currentQCM?.exam_id || sessionContext?.examId}
                            userId={sessionContext?.userId}
                            onItemSelect={handleSearchItemSelect}
                        />
                    </div>
                    
                    {/* What is? button with search icon - always visible */}
                    {!showInput && (
                        <button
                            onClick={() => setShowInput(true)}
                            className="flex items-center gap-1 text-sm text-gray-400 hover:text-blue-300 transition-colors whitespace-nowrap"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            Ask something
                        </button>
                    )}
                </div>

                {/* Input field - only show when activated */}
                {showInput && (
                    <div className="flex items-center gap-2 -mt-2">
                        <input
                            ref={textInputRef}
                            type="text"
                            value={question}
                            onChange={handleQuestionChange}
                            onKeyPress={handleKeyPress}
                            placeholder="Prefer keywords, ex: Event vs Message"
                            className="w-80 text-base bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 focus:outline-none focus:border-blue-500"
                            disabled={isLoading}
                            maxLength={MAX_QUESTION_LENGTH}
                        />
                        {errorMessage && (
                            <div className="text-xs text-red-400 mt-1">
                                {errorMessage}
                            </div>
                        )}
                        <button
                            onClick={() => setShowInput(false)}
                            className="text-xs text-gray-400 hover:text-gray-300"
                        >
                            ✕
                        </button>
                    </div>
                )}

                {/* My Questions button - always visible */}
                <button
                    onClick={() => setShowQuestionsList(!showQuestionsList)}
                    className="flex items-center gap-1 text-sm text-gray-400 hover:text-blue-300 transition-colors"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                    My previous questions
                </button>
            </div>

            {/* Questions List - 3 Column Grid with Search */}
            {showQuestionsList && (
                <div className="absolute top-20 right-4 z-50 bg-gray-800 rounded-lg shadow-2xl p-4 max-w-2xl">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-white">My Previous Questions</h3>
                        <button
                            onClick={() => setShowQuestionsList(false)}
                            className="text-gray-400 hover:text-white"
                        >
                            ✕
                        </button>
                    </div>
                    
                    {/* Search Input */}
                    <div className="relative mb-3">
                        <input
                            type="text"
                            placeholder="Search questions..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-3 py-2 pl-8 bg-gray-700 text-white text-sm rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                        />
                        <svg className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    
                    <div 
                        className="max-h-60 overflow-y-auto"
                        style={{
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#4B5563 #1F2937'
                        }}
                    >
                        {filteredQuestions.length === 0 ? (
                            <p className="text-gray-400 text-sm">
                                {searchQuery ? 'No questions found' : 'No questions yet'}
                            </p>
                        ) : (
                            <div className="grid grid-cols-3 gap-2">
                                {filteredQuestions.map((item, index) => (
                                    <div
                                        key={item.id}
                                        onClick={async () => {
                                            // Check if post-it already exists
                                            const existingPostIt = postIts.find(p => p.id === item.id);
                                            
                                            if (!existingPostIt) {
                                                // Load full content from backend using the specific question ID
                                                const fullQuestion = await loadQuestionContent(item.id);
                                                const answerText = fullQuestion?.answer || item.answer || 'Content not available';
                                                
                                                // Create new post-it with full content
                                                const newPostIt = createNewPostIt(item.question, answerText, item.id);
                                                setPostIts([...postIts, newPostIt]);
                                            }
                                            
                                            setShowQuestionsList(false);
                                        }}
                                        className="p-3 bg-gray-700 rounded cursor-pointer hover:bg-gray-600 transition-colors"
                                    >
                                        <div className="text-sm text-blue-300 font-medium truncate">
                                            {item.question}
                                        </div>
                                        <div className="text-sm text-gray-400 mt-1">
                                            {new Date(item.timestamp).toLocaleDateString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Render all Post-its */}
            {postIts.map(postIt => (
                <PostItComponent
                    key={postIt.id}
                    postIt={postIt}
                    onDelete={deletePostIt}
                    onUpdate={updatePostIt}
                />
            ))}
        </>
    );
};

export default OpenQuestion;